import React from 'react';

import { getServerErrorData } from '@lumapps/base-api/utils/getServerError';
import { BeekeeperContext, CommonContext } from '@lumapps/constants/types';
import { customerSelector } from '@lumapps/customer/ducks/selectors';
import { getCurrentInstance } from '@lumapps/instance/ducks/selectors';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { useSelector, useStore } from '@lumapps/redux/react';
import { BaseStore } from '@lumapps/redux/types';
import { Route } from '@lumapps/router';
import { useRouter } from '@lumapps/router/hooks/useRouter';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { isBeekeeperIntegrationEnabled } from '../ducks/selectors';
import { initBeekeeperContext } from '../utils/initBeekeeperContext';
import { initRouterBridge } from '../utils/initRouterBridge';

export interface UseBeekeeperContextProps {
    // Callback called when the context is fully initialized
    onDone?: (context?: CommonContext) => void;
    // Callback called before marking the context as initialized
    onBeforeDone?: (context?: CommonContext) => Promise<void>;
    // Name of the component to be rendered (used to load the remote entry point)
    componentName?: string;
}

export interface UseBeekeeperContext {
    status: BaseLoadingStatus;
    context?: BeekeeperContext;
    isEnabled?: boolean;
    error?: string;
}

/**
 * Hook to initialize the Beekeeper context
 * @param param0 Callbacks to be called during the context initialization
 */
export const useBeekeeperContext = (options?: UseBeekeeperContextProps): UseBeekeeperContext => {
    const { onDone, onBeforeDone = () => Promise.resolve(), componentName } = options || {};
    const router = useRouter();
    const store = useStore<BaseStore>();
    const notifications = useNotification();
    const isEnabled = useSelector(isBeekeeperIntegrationEnabled);
    const { slug } = useSelector(getCurrentInstance);
    const { slug: customerSlug } = useSelector(customerSelector);
    const [status, setStatus] = React.useState<BaseLoadingStatus>(BaseLoadingStatus.initial);
    const [error, setError] = React.useState<string | undefined>();

    const getCurrentRoute = React.useCallback(() => {
        const state = store.getState();
        const { currentRoute, previousRoute } = state.router;
        // assume currentRoute is always defined after initialization
        return { currentRoute: currentRoute as Route, previousRoute };
    }, [store]);

    React.useEffect(
        () => {
            if (isEnabled) {
                setStatus(BaseLoadingStatus.loading);

                Promise.all([
                    // Init the beekeeper context
                    initBeekeeperContext({ notifications, slug, router }),
                    // Init the router bridge
                    initRouterBridge({ router, getCurrentRoute, instanceSlug: slug, customerSlug }),
                ])
                    .then(() => {
                        // Execute the onBeforeDone callback (e.g. load the remote entry point)
                        onBeforeDone(window.BeekeeperContext)
                            .then(() => {
                                setStatus(BaseLoadingStatus.idle);
                                onDone?.(window.BeekeeperContext);
                            })
                            .catch((error) => {
                                // eslint-disable-next-line no-console
                                console.log(error);
                                setStatus(BaseLoadingStatus.error);
                            });
                    })
                    .catch((error) => {
                        const errorLog = getServerErrorData(error.response);
                        setStatus(BaseLoadingStatus.error);
                        setError(errorLog?.title);
                    });
            }
        },
        // Init must be run only when the component name changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [componentName],
    );

    return { status, context: window.BeekeeperContext, isEnabled, error };
};
